import React, { useEffect, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  Circle,
  CircleMarker,
  Tooltip,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  getInitials,
  getMarkers,
  getPOSPSName,
  numberCurrencyFormatInTable,
} from "../../utils/Util.tsx";
import ColorRangeBar from "./ColorRangeBar.tsx";
import {
  ACTIVE_VIEW_COUNTRY,
  ACTIVE_VIEW_STATE,
  COMBINED_LEVELS_FILTER,
  defaultPosition,
  MARKER_DETAILS,
  MARKER_TYPE,
  SCREEN_TYPE,
} from "../../utils/Constants.tsx";
import CountryVectorLayer from "./CountryVectorLayer.tsx";
import StateVectorLayer from "./StateVectorLayer.tsx";
import { paths } from "../../services/ServiceConstants.tsx";
import WmsLayer from "./WmsLayer.tsx";
import WeatherLegend from "./WeatherLegend.tsx";

interface MapProps {
  activeView;
  markerType?: string;
  markerData?: any[];
  districtId: string;
  setMapClickedInfo?: () => void;
  mapClickedInfo: any;
  mapToolTip: string;
  showGridToolTip: string;
  filterName: string;
  idWiseData?: any[];
  legendStyle?: any;
  legendValues: any;
  state?: any;
  district: any;
  distance?: number;
  selectedGeometry: any;
  mapView: string;
  countryGridData: any;
  gridCenter: any;
  screenType?: string;
  selectedFilters: any;
  computedDate: string;
  forecastDate: string;
  onMarkerClick?: (markerData: any, keyName: String) => void;
}
const Map: React.FunctionComponent<MapProps> = ({
  activeView,
  markerType,
  markerData,
  districtId,
  setMapClickedInfo,
  mapClickedInfo,
  mapToolTip,
  showGridToolTip,
  filterName,
  idWiseData,
  legendStyle,
  legendValues,
  state,
  district,
  distance,
  selectedGeometry,
  mapView,
  countryGridData,
  gridCenter,
  screenType,
  selectedFilters,
  computedDate,
  forecastDate,
  onMarkerClick,
}) => {
  const [markers, setMarkers] = useState<
    { position: L.LatLng; markerData: any }[]
  >([]);
  const [mapCenter, setMapCenter] = useState(null);
  const [isSelectorAdded, setSelectorAdded] = useState(false);
  const [stateNameFontSize, setStateNameFontSize] = useState(10);

  const [wmsTileLayer, setWmsTileLayer] = useState(null);
  const [stateVectorTileLayer, setStateVectorTileLayer] = useState(null);
  const [countryVectorGridLayer, setCountryVectorGridLayer] = useState(null);
  const [countryStateVectorLayer, setCountryStateVectorLayer] = useState(null);

  useEffect(() => {
    if (mapClickedInfo?.lat && mapClickedInfo?.lng) {
      setMapCenter([mapClickedInfo.lat, mapClickedInfo.lng]);
    } else if (district && district?.vcentroidlat && district?.vcentroidlon) {
      setMapCenter([district.vcentroidlat, district.vcentroidlon]);
    } else if (state && state?.vcentroidlat && state?.vcentroidlon) {
      setMapCenter([state.vcentroidlat, state.vcentroidlon]);
    }
    setSelectorAdded(false);
  }, [state, district, mapClickedInfo]);

  /**
   * This is zoom watcher map child component
   * @returns
   */
  function ZoomWatcher() {
    const map = useMap();
    useEffect(() => {
      const onZoomEnd = () => {
        const zoomLevel = map.getZoom();
        if (zoomLevel > 9) {
          setStateNameFontSize(16);
        } else if (zoomLevel > 7) {
          setStateNameFontSize(14);
        } else if (zoomLevel > 5) {
          setStateNameFontSize(12);
        } else if (zoomLevel > 4) {
          setStateNameFontSize(10);
        } else {
          setStateNameFontSize(0);
        }
      };
      map.on("zoomend", onZoomEnd);
      return () => {
        map.off("zoomend", onZoomEnd);
      };
    }, [map]);

    return null;
  }

  /**
   * Remove added old layers
   */
  const clearLayers = (map) => {
    if (map) {
      if (stateVectorTileLayer) {
        map.eachLayer((layer) => {
          if (layer._url === stateVectorTileLayer._url) {
            map.removeLayer(layer);
          }
        });
        map.removeLayer(stateVectorTileLayer);
        setStateVectorTileLayer(null);
      }

      if (countryStateVectorLayer) {
        map.eachLayer((layer) => {
          if (layer._url === countryStateVectorLayer._url) {
            map.removeLayer(layer);
          }
        });
        map.removeLayer(countryStateVectorLayer);
        setCountryStateVectorLayer(null);
      }

      if (countryVectorGridLayer) {
        map.eachLayer((layer) => {
          if (layer._url === countryVectorGridLayer._url) {
            map.removeLayer(layer);
          }
        });
        map.removeLayer(countryVectorGridLayer);
        setCountryVectorGridLayer(null);
      }

      if (wmsTileLayer) {
        map.eachLayer((layer) => {
          if (layer._url === wmsTileLayer._url) {
            map.removeLayer(layer);
          }
        });
        map.removeLayer(wmsTileLayer);
        setWmsTileLayer(null);
      }
    }
  };
  useEffect(() => {
    try {
      if (markerType?.length > 0 && markerType[0] !== "waterbodies") {
        if (markerData?.length > 0) {
          const coordinates = markerData
            ?.flatMap((markerGroup: any) =>
              markerGroup?.res
                ?.map((item: any) => {
                  let markerCoordinates;
                  if (
                    markerGroup.marker === MARKER_TYPE.STATE &&
                    item?.latitude &&
                    item?.longitude
                  ) {
                    markerCoordinates = {
                      mType: "stateName",
                      position: L.latLng(
                        parseFloat(item?.latitude),
                        parseFloat(item?.longitude),
                      ),
                      icon: L.divIcon({
                        className: "state-label",
                        html: `<div style="font-size:${stateNameFontSize}px">${item?.state}</div>`,
                        iconSize: [100, 20],
                        iconAnchor: [50, 10],
                      }),
                      markerData: item,
                      markerType: markerGroup.marker,
                    };
                  } else if (
                    markerGroup.marker === MARKER_TYPE.POSPS &&
                    item?.latitude &&
                    item?.longitude
                  ) {
                    markerCoordinates = {
                      position: L.latLng(
                        parseFloat(item?.latitude),
                        parseFloat(item?.longitude),
                      ),
                      icon: L.divIcon({
                        className: "posps-marker-icon",
                        html: `
                        <div class="marker-wrapper">
                          <div class="${
                            item.rolename.includes("Manager")
                              ? "circle-ripple-pospm"
                              : "circle-ripple-posp"
                          }"></div>
                          <div class="marker-content">
                            <div class="marker-image">
                              <img src="${item?.photo}" class="round-image"
                                onerror="this.style.display='none';" />
                            </div>
                            <div class="marker-text ${
                              item.rolename.includes("Manager")
                                ? "marker-text-bg-pospm"
                                : "marker-text-bg-posp"
                            }">
                              ${getInitials(item?.employeename)}
                            </div>
                          </div>
                        </div>
                      `,
                        iconSize: [80, 80],
                        iconAnchor: [40, 40],
                      }),

                      markerData: item,
                      markerType: markerGroup.marker,
                    };
                  } else {
                    const markerInfo = getMarkers(markerGroup.marker, item);
                    markerCoordinates = markerInfo
                      ? {
                          mType: "icon",
                          position: markerInfo.position,
                          icon: L.icon({
                            iconUrl: markerInfo.icon,
                            iconSize: [32, 32],
                            iconAnchor: [16, 32],
                            popupAnchor: [0, -32],
                          }),
                          markerData: item,
                          markerType: markerGroup.marker,
                        }
                      : null;
                  }
                  return markerCoordinates;
                })
                .filter(Boolean),
            )
            .filter(Boolean); // Ensure no undefined values in the final array
          setMarkers(coordinates);
        }
      } else {
        setMarkers(undefined);
      }
    } catch (err) {}
  }, [markerData, markerType, stateNameFontSize]);

  const groupIcon = L.icon({
    iconUrl: require("../../assets/images/group.svg").default,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });

  const getLegendsView = () => {
    // ✅ Allow 0 as a valid min/max by checking types instead of truthiness
    const canShowLegend =
      legendStyle &&
      legendStyle.minColor &&
      legendStyle.maxColor &&
      typeof legendStyle.minValue === "number" &&
      typeof legendStyle.maxValue === "number";
    console.log(legendStyle, "legendstyle");

    // console.log(legendStyle?.maxColor, "legendStyle?.maxColor");
    // console.log(
    //   legendStyle?.maxValue,
    //   idWiseData,
    //   "legendStyle?.maxValue and id wise data",
    // );
    return (
      <>
        {canShowLegend && (
          <ColorRangeBar
            minColor={legendStyle.minColor}
            maxColor={legendStyle.maxColor}
            minValue={legendStyle.minValue}
            maxValue={legendStyle.maxValue}
          />
        )}
        <ZoomWatcher />
      </>
    );
  };

  const getMapMarkers = () => {
    return (
      <>
        {markers &&
          filterName &&
          markers.map((marker: any, index: any) => (
            <React.Fragment key={`FragmentKey-${index.toString()}`}>
              <Marker
                eventHandlers={{
                  click: () => {
                    onMarkerClick(marker?.markerData, "employeeuniqueid");
                  },
                }}
                position={marker.position}
                icon={
                  marker?.markerData?.posp_count > 1 &&
                  marker.markerType === MARKER_TYPE.POSPS
                    ? groupIcon
                    : marker?.icon
                }
              ></Marker>
            </React.Fragment>
          ))}
      </>
    );
  };
  const getCountryViewMap = () => {
    return (
      <MapContainer
        center={defaultPosition}
        zoom={5}
        className="map-parent"
        id="map"
      >
        <TileLayer url={paths.TILE_URL} maxZoom={19} />
        {filterName === COMBINED_LEVELS_FILTER.Weather_Rainfall && (
          <>
            <WmsLayer
              activeView={activeView}
              setWMSTileLayer={setWmsTileLayer}
              clearLayers={clearLayers}
              selectedFilters={selectedFilters}
              computedDate={computedDate}
              forecastDate={forecastDate}
              setIsLoading={() => {}}
              setShowLegend={() => {}}
              setMapClickedInfo={() => {}}
              onWMSLoadComplete={() => {}}
            />
            <WeatherLegend />
          </>
        )}

        {filterName !== COMBINED_LEVELS_FILTER.Weather_Rainfall && (
          <>
            {idWiseData && (
              <CountryVectorLayer
                filterName={filterName}
                idWiseData={idWiseData}
                legendValues={legendValues}
                mapView={mapView}
                setMapClickedInfo={setMapClickedInfo}
                selectedGeometry={selectedGeometry}
                gridCenter={gridCenter}
                setCountryStateVectorLayer={setCountryStateVectorLayer}
                setCountryVectorGridLayer={setCountryVectorGridLayer}
                clearLayers={clearLayers}
              />
            )}
            {mapClickedInfo && distance && (
              <Circle
                center={[mapClickedInfo.lat, mapClickedInfo.lng]}
                radius={distance * 1000}
              />
            )}
          </>
        )}

        {getMapMarkers()}
        {getLegendsView()}
      </MapContainer>
    );
  };
  const getStateViewMap = () => {
    return (
      <MapContainer
        center={defaultPosition}
        zoom={screenType === SCREEN_TYPE.NORMAL ? 6 : 5}
        className="map-parent"
        id="map"
      >
        <TileLayer url={paths.TILE_URL} maxZoom={19} />

        {state && (
          <StateVectorLayer
            screenType={screenType}
            mapCenter={mapCenter}
            idWiseData={idWiseData}
            stateId={state?.sid}
            districtId={district?.did}
            setMapClickedInfo={setMapClickedInfo}
            legendValues={legendValues}
            selectedGeometry={selectedGeometry}
            onSelectorAdded={setSelectorAdded}
            gridCenter={gridCenter}
            state={stateVectorTileLayer}
            setVectorTileLayer={setStateVectorTileLayer}
            clearLayers={clearLayers}
            filterName={filterName}
          />
        )}
        {mapClickedInfo && distance && (
          <Circle
            center={[mapClickedInfo.lat, mapClickedInfo.lng]}
            radius={distance * 1000}
          />
        )}

        {isSelectorAdded &&
          showGridToolTip &&
          mapToolTip?.gridid &&
          mapClickedInfo?.lat &&
          mapClickedInfo?.lng && (
            <CircleMarker
              center={[mapClickedInfo.lat, mapClickedInfo.lng]}
              pathOptions={{ color: "transparent" }}
              radius={20}
            >
              <Tooltip offset={[0, 20]} opacity={1}>
                <>
                  Gird Id: {mapToolTip?.gridid}
                  <br />
                  {filterName === "POSPs" && (
                    <>
                      POSPs:{" "}
                      {mapToolTip?.posps === null
                        ? "null"
                        : mapToolTip?.posps?.count
                          ? getPOSPSName(
                              mapToolTip?.posps[MARKER_DETAILS.posps],
                            )
                          : ""}
                      <br />
                    </>
                  )}
                  {filterName === "Premium" && (
                    <>
                      Premium:{" "}
                      {mapToolTip?.premium === null
                        ? "null"
                        : numberCurrencyFormatInTable(mapToolTip, "premium")}
                      <br />
                    </>
                  )}
                  {filterName === "Sum Insured" && (
                    <>
                      Sum Insured:{" "}
                      {mapToolTip?.suminsured === null
                        ? "null"
                        : numberCurrencyFormatInTable(mapToolTip, "suminsured")}
                      <br />
                    </>
                  )}
                  {filterName === "Policies" && (
                    <>
                      Policy Count:{" "}
                      {mapToolTip?.policies?.count === null
                        ? "null"
                        : mapToolTip?.policies?.count}
                      <br />
                    </>
                  )}
                </>
              </Tooltip>
            </CircleMarker>
          )}
        {getMapMarkers()}
        {getLegendsView()}
      </MapContainer>
    );
  };
  return (
    <>
      {activeView === ACTIVE_VIEW_COUNTRY && getCountryViewMap()}
      {activeView === ACTIVE_VIEW_STATE && getStateViewMap()}
    </>
  );
};

export default Map;
